import { useImperativeHandle } from 'react';
import * as v from 'valibot';
import { useForm, useStore } from '@tanstack/react-form';

import { Button, InputField, SelectField } from '@nvision/ui';

import { useCountries, useStates } from '../../../hooks';
import { type AddressSearchFormValues, addressSearchFormSchema, type Country, type State } from '../../../models';

import styles from './address-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface AddressSearchFormProps {
  additionalActions?: React.ReactNode;
  defaultValues?: Partial<AddressSearchFormValues>;
  formRef: React.RefObject<SearchFormRef>;
  handleReset: () => void;
  handleSubmit?: (values: AddressSearchFormValues) => void;
}

const getDefaultSearchFormValues = (): AddressSearchFormValues =>
  v.getDefaults(addressSearchFormSchema) as unknown as AddressSearchFormValues;

export const AddressSearchForm: React.FC<AddressSearchFormProps> = ({
  additionalActions,
  defaultValues,
  formRef,
  handleReset,
  handleSubmit,
}) => {
  const form = useForm({
    defaultValues: defaultValues || getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit?.(props.value);
    },
    validators: {
      onChange: addressSearchFormSchema,
      onSubmit: addressSearchFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      handleReset();
    },
    submit: () => {
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const { data: countries, isLoading: countriesLoading } = useCountries();
  const country = useStore(form.store, (store) => store.values.country);
  const { data: states, isLoading: statesLoading } = useStates({
    CountryCode: country?.id ?? '',
  });

  const handleCountryChange = (country: Country | null, onChange: (value: { id: string; label: string }) => void) => {
    if (country) {
      onChange({ id: String(country.id), label: country.label });
      form.setFieldValue('state', { id: '', label: '' });
    }
  };

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <div className={styles.fields}>
        <form.Field name="country">
          {(field) => (
            <SelectField
              className={styles.fieldCountry}
              value={field.state.value as unknown as Country | null}
              label="Country"
              options={countries?.list ?? []}
              placeholder="Select"
              size="sm"
              onBlur={field.handleBlur}
              onSelectionChange={(c) => handleCountryChange(c, field.handleChange)}
              isLoading={countriesLoading}
            />
          )}
        </form.Field>
        <form.Field name="state">
          {(field) => (
            <SelectField
              className={styles.fieldState}
              value={field.state.value as unknown as State | null}
              label="State/Province"
              options={states?.list ?? []}
              placeholder="Select"
              size="sm"
              onBlur={field.handleBlur}
              onSelectionChange={(s) => s && field.handleChange({ id: String(s.id), label: s.label })}
              isLoading={statesLoading}
            />
          )}
        </form.Field>
        <form.Field name="postalCode">
          {(field) => (
            <InputField
              className={styles.fieldPostal}
              label="Postal Code"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="city">
          {(field) => (
            <InputField
              className={styles.fieldCity}
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              label="City"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="name">
          {(field) => (
            <InputField
              className={styles.fieldCompanyName}
              label="Company Name"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="address1">
          {(field) => (
            <InputField
              className={styles.fieldAddress1}
              label="Address 1"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="address2">
          {(field) => (
            <InputField
              className={styles.fieldAddress2}
              label="Address 2"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <div className={styles.actions}>
          <Button type="submit">Search</Button>
          <Button outline secondary type="button" onClick={() => formRef.current?.reset()}>
            Reset
          </Button>
          {additionalActions}
        </div>
      </div>
    </form>
  );
};
